import type { TopicData } from "@/shared/state/visualization";
import type {
  VectorField,
  VectorFieldDisplayMode,
} from "@/shared/state/visualization/schema/v1/three-d";

/**
 * Parses a text input value as a finite number, returning a discriminated
 * union with either the parsed value or an error message.
 */
export const parseNumberInput = (
  value: string,
  label: string,
):
  | { ok: true; value: number; error?: undefined }
  | { ok: false; error: string } => {
  // Note: parseFloat stops at the first invalid character, so "1.2.3" parses
  // as 1.2 without error.
  const parsed = Number.parseFloat(value);
  if (Number.isFinite(parsed)) {
    return { ok: true, value: parsed };
  }
  return { ok: false, error: `${label} must be a number.` };
};

/**
 * Validates a parsed stride value. Returns an error message if invalid,
 * or undefined if valid.
 */
export function validateStride(value: number): string | undefined {
  if (value < 1) {
    return "Stride must be at least 1.";
  }
  if (!Number.isInteger(value)) {
    return "Stride must be a whole number.";
  }
  return undefined;
}

/**
 * Input fields for building a VectorField spec. These correspond to
 * the parsed values from the config panel's text fields and selectors.
 */
export interface VectorFieldInputs {
  forwardData: TopicData | undefined;
  rightData: TopicData | undefined;
  upData: TopicData | undefined;
  forwardScale: number;
  rightScale: number;
  upScale: number;
  lineWidth: number;
  stride: number;
  color: string;
  displayMode: VectorFieldDisplayMode;
  gradientEnabled: boolean;
  colorLow: string;
  colorHigh: string;
}

/**
 * Builds a VectorField spec from parsed inputs, or returns null if
 * no data sources are set (nothing to dispatch).
 *
 * Falls back to the existing spec's axis bindings when a data source
 * is not set, then to the constant 0.
 */
export function buildVectorFieldSpec(
  inputs: VectorFieldInputs,
  existing: VectorField | undefined,
): VectorField | null {
  // Require at least one data source to be set before building a spec.
  if (!inputs.forwardData && !inputs.rightData && !inputs.upData) {
    return null;
  }

  return {
    forward: inputs.forwardData ?? existing?.forward ?? 0,
    forwardScale: inputs.forwardScale,
    right: inputs.rightData ?? existing?.right ?? 0,
    rightScale: inputs.rightScale,
    up: inputs.upData ?? existing?.up ?? 0,
    upScale: inputs.upScale,
    color: inputs.color,
    lineWidth: inputs.lineWidth,
    // Omit optional fields when they match their defaults so the persisted
    // shape stays minimal and the isEqual check doesn't fire on mount.
    stride: inputs.stride === 1 ? undefined : inputs.stride,
    displayMode:
      inputs.displayMode === "current_only" ? undefined : inputs.displayMode,
    gradientEnabled: inputs.gradientEnabled ? true : undefined,
    // Always persist gradient colors so they aren't lost when the user
    // toggles the gradient off and back on. The rendering pipeline gates
    // gradient usage on `gradientEnabled`, not on color presence.
    colorLow: inputs.colorLow,
    colorHigh: inputs.colorHigh,
  };
}
